import React, { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import DrillPanel from './DrillPanel';
import { makeInfluenceDrillConfig } from './drillConfigs';
import useInfluenceBlock from '../../hooks/useInfluenceBlock';
import { ExecKpiRow, ExecKpiSlot, KpiTile } from './ExecKpiRow';
import { formatCurrency } from '../../utils/formatCurrency';
import { fmtPts, fmtPct, fmtInt, fmtInterval } from '../../utils/influenceScorecard';
import { pendingDealsText, domainMatchNote, EXPLORATORY_LABEL, isExploratory, drillOn } from '../../utils/influenceBlocks';
import { kpiReasonText, liftLines, speedLines, sizeLines } from '../../utils/influenceKpis';

const money = (v) => formatCurrency(v, { emptyDisplay: '—' });
const Line = ({ children }) => <Box component="span" sx={{ display: 'block' }}>{children}</Box>;
const lines = (arr) => arr.filter(Boolean).map((t, i) => <Line key={i}>{t}</Line>);
const ExploratoryTag = () => (
  <Box component="span" data-testid="exploratory-label" sx={{ fontWeight: 700, color: 'warning.dark' }}>{EXPLORATORY_LABEL}</Box>
);

const TIER_NAME = { program: 'TEC Program influenced', product: 'TEC Product attributed' };
const REACH = 'Influenced means reach: an SE/technical team member or contact attended a TEC workshop or event before the deal closed. It is not revenue TEC created.';

function tipFor(id, tier) {
  const scope = tier === 'product'
    ? 'Matching SKU lines only (the products an attended workshop or event covers).'
    : 'Whole deal amount of every influenced deal.';
  const cmp = 'Compared with deals of the same age and size where no SE who attended TEC was on the deal team, closed deals only, 95% interval; an association, not proof of effect.';
  return {
    pipeline: `${REACH} ${scope} Includes open, won and lost deals created in the period. Customer-account and partner paths are shown separately, never added, and count confirmed domain matches only (${domainMatchNote}).`,
    won: `${REACH} Closed-won amount of those deals created in the period (PO Issued / PO Received count as won).`,
    share: 'Influenced deals and their value as a share of all in-scope deals and value created in the same period: how much of the book TEC reaches, so 5% and 50% do not read the same.',
    lift: `Pooled win-rate difference within 180 days across all influenced deals. ${cmp}`,
    speed: `Share of deals that closed (won or lost) within 180 days, influenced versus comparable deals at the same age and size. ${cmp} The median days to close is among deals that closed within 180 days.`,
    size: `Average won amount, influenced versus comparable won deals at the same age and size band. ${cmp}`,
  }[id];
}

function Unavailable({ data, error }) {
  const reason = error ? `the request failed (${error})` : kpiReasonText(data?.reason);
  if (!error && data?.reason === 'influence_reloading') {
    return <Alert severity="info" sx={{ mb: 2 }} aria-label="Influence KPI cards unavailable">{data.message}</Alert>;
  }
  return (
    <Alert severity="info" sx={{ mb: 2 }} aria-label="Influence KPI cards unavailable">
      The six influence KPI cards are not available for this view: {reason}.
    </Alert>
  );
}

// A path line ("Customer-account path (separate): $X") that opens its own deal list instead of the card's.
function PathLine({ children, onOpen }) {
  if (!onOpen) return children;
  return (
    <Box component="span" role="button" tabIndex={0} sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onOpen(); } }}>{children}</Box>
  );
}

// kpis = useInfluenceKpis() state; onCardClick = the click-through to the TEC Influence page (omit on that page). Renders nothing while loading, and nothing when unavailable unless showUnavailable
// (the pages that keep their legacy tiles say why there themselves).
// drill = { surface, scope } (plan 2213 phase 4): the pipeline, won and share cards, and their customer-account / partner / pending lines, open
// the deal list behind the number inline instead of navigating; `scope` is the filter set the cards were requested with.
export default function InfluenceKpiCards({ kpis, tier = 'program', onCardClick, showUnavailable = false, drill = null }) {
  const status = useInfluenceBlock('/exec/influence/status', {}, !!drill);
  const [open, setOpen] = useState(null); // { of, state, title }
  if (!kpis || (kpis.loading && !kpis.data)) return null;
  const { data, error } = kpis;
  if (error || !data?.available || !Array.isArray(data.cards)) return showUnavailable ? <Unavailable data={data} error={error} /> : null;
  const by = Object.fromEntries(data.cards.map((c) => [c.id, c]));
  const tierName = TIER_NAME[data.tier || tier];
  const canDrill = !!drill && drillOn(status.data, drill.surface);
  const openList = (of, state, title) => () => setOpen((prev) => (prev && prev.of === of && prev.state === state ? null : { of, state, title }));
  const tierOf = (data.tier || tier) === 'product' ? 'tier:deep_dive' : 'tier:program';
  const go = onCardClick;
  const onPipeline = canDrill ? openList(tierOf, 'all', `${tierName} — influenced deals`) : go;
  const onWon = canDrill ? openList(tierOf, 'won', `${tierName} — won influenced deals`) : go;
  const onShare = canDrill ? openList(tierOf, 'all', `${tierName} — influenced deals`) : go; // the share card counts the same tier block as the pipeline card
  const path = (key, state, title, text) => (canDrill ? <PathLine onOpen={openList(`path:${key}`, state, title)}>{text}</PathLine> : text);
  const tip = (id) => `${tipFor(id, data.tier || tier)} ${data.basis_line}.`;
  const pipe = by.pipeline;
  const won = by.won;
  const share = by.share;
  const lift = liftLines(by.lift);
  const speed = speedLines(by.speed);
  const size = sizeLines(by.size);
  const l365 = by.lift.lift_365;
  const lm = by.lift.landmark;
  const muted = (ins) => (ins ? 'text.disabled' : undefined);
  const expl = isExploratory(data) ? <ExploratoryTag /> : null; // model metrics on a non-standard period (plan 2213 D3)

  return (
    <Box aria-label="Influence KPI cards" data-testid="influence-kpi-cards">
      <ExecKpiRow>
        <ExecKpiSlot basis={220}>
          <KpiTile
            label="Influenced pipeline"
            value={money(pipe.value)}
            color="#1976d2"
            sub={`${tierName} · ${fmtInt(pipe.n_deals)} deals · all stages · reach, not revenue`}
            sub2={lines([
              pipe.customer_account?.value != null && path('customer_account_confirmed', 'all', 'Customer-account path — deals', `Customer-account path (separate): ${money(pipe.customer_account.value)}`),
              pipe.partner?.value != null && path('partner_confirmed', 'all', 'Partner path — deals', `Partner path (separate): ${money(pipe.partner.value)}`),
              pipe.pending_review?.n_deals ? path('pending_review', 'all', 'Pending review — deals (not counted)', pendingDealsText(pipe.pending_review)) : null,
            ])}
            aria-label="TEC-influenced pipeline card" tooltip={tip('pipeline')} onClick={onPipeline}
          />
        </ExecKpiSlot>
        <ExecKpiSlot basis={200}>
          <KpiTile
            label="Influenced won"
            value={money(won.value)}
            color="#2e7d32"
            sub={`${tierName} · ${fmtInt(won.n_won)} won deals`}
            sub2={lines([
              won.customer_account?.value != null && path('customer_account_confirmed', 'won', 'Customer-account path — won deals', `Customer-account path (separate): ${money(won.customer_account.value)}`),
              won.partner?.value != null && path('partner_confirmed', 'won', 'Partner path — won deals', `Partner path (separate): ${money(won.partner.value)}`),
            ])}
            aria-label="TEC-influenced won card" tooltip={tip('won')} onClick={onWon}
          />
        </ExecKpiSlot>
        <ExecKpiSlot basis={180}>
          <KpiTile
            label="Share of all deals"
            value={fmtPct(share.deals_pct, 1)}
            sub={`${fmtInt(share.n_deals)} of ${fmtInt(share.total_deals)} deals`}
            sub2={lines([`${fmtPct(share.value_pct, 1)} of all deal value (${money(share.total_value)})`])}
            aria-label="Share of all deals card" tooltip={tip('share')} onClick={onShare}
          />
        </ExecKpiSlot>
        <ExecKpiSlot basis={220}>
          <KpiTile
            label={`Win-rate lift (180 days)${tier === 'product' ? ' · program-level' : ''}`}
            value={lift.value}
            color={lift.insufficient ? undefined : '#2e7d32'}
            valueStyle={lift.insufficient ? { fontSize: '1.2rem', color: muted(true) } : undefined}
            sub={lift.lines[0]}
            sub2={lines([
              expl,
              ...lift.lines.slice(1),
              !lift.insufficient && lm && !lm.insufficient && `Check, deals already open at first attendance: ${fmtPts(lm.lift_pts)} ${fmtInterval(lm.ci_lo_pts, lm.ci_hi_pts)}, n = ${fmtInt(lm.n)}`,
              !lift.insufficient && l365 && `At 365 days: ${fmtPts(l365.lift_pts)} ${fmtInterval(l365.ci_lo_pts, l365.ci_hi_pts)} (default window)`,
              by.lift.tier_note,
              !lift.insufficient && 'Association, not proof of effect',
            ])}
            aria-label="Win-rate lift card" tooltip={tip('lift')} metricKey="pooled_lift" onClick={go}
          />
        </ExecKpiSlot>
        <ExecKpiSlot basis={220}>
          <KpiTile
            label={`Speed to close (180 days)${tier === 'product' ? ' · program-level' : ''}`}
            value={speed.value}
            valueStyle={speed.insufficient ? { fontSize: '1.2rem', color: muted(true) } : undefined}
            sub={speed.lines[0]}
            sub2={lines([expl, ...speed.lines.slice(1), by.speed.tier_note])}
            aria-label="Speed to close card" tooltip={tip('speed')} onClick={go}
          />
        </ExecKpiSlot>
        <ExecKpiSlot basis={220}>
          <KpiTile
            label={`Deal size (won deals)${tier === 'product' ? ' · program-level' : ''}`}
            value={size.value}
            valueStyle={size.insufficient ? { fontSize: '1.2rem', color: muted(true) } : undefined}
            sub={size.lines[0]}
            sub2={lines([expl, ...size.lines.slice(1), by.size.tier_note])}
            aria-label="Deal size card" tooltip={tip('size')} onClick={go}
          />
        </ExecKpiSlot>
      </ExecKpiRow>
      <Typography variant="caption" color="text.secondary" aria-label="Influence KPI cards basis" sx={{ display: 'block', mt: -1, mb: 2 }}>
        {data.basis_line} · influenced = reach, not revenue TEC created{canDrill ? ' · click the pipeline, won or share card for the deals behind it' : ''}
      </Typography>
      {canDrill && open && (
        <Box sx={{ mb: 2 }}>
          <DrillPanel
            key={`${open.of}|${open.state}`}
            title={open.title}
            filters={{}}
            startDate={drill.scope.startDate}
            endDate={drill.scope.endDate}
            windowMonths={0}
            postWorkshopOnly={false}
            panelConfig={makeInfluenceDrillConfig(open.of, { ...drill.scope, surface: drill.surface }, { state: open.state })}
            onClose={() => setOpen(null)}
          />
        </Box>
      )}
    </Box>
  );
}
